import { injectable } from 'inversify';
import { dumbConfig } from './modules.helper';
import { InputType, SpecType } from './module.enum';

import glasgowCommaScaleSpecs from './glasgowComaScale/glasgow_coma_scale_v1.0.json';
import immediateMemorySpecs from './immediateMemory/immediate_memory_v1.0.json';
import symptomsChecklistSpecs from './symptomsChecklist/symptom_checklist_v1.0.json';
import digitsBackwardsSpecs from './digitsBackwards/digits_backwards_v1.0.json';
import neckExamSpecs from './neckExam/neck_exam_v1.0.json';
import neurologicalExamSpecs from './neurologicalExam/neuro_exam_v1.0.json';
import singleLegExamSpecs from './balanceSingleLegStance/balance_single_leg_stance_v1.0.json';
import doubleLegExamSpecs from './balanceDoubleLegStance/balance_double_leg_stance_v1.0.json';
import tandemExamSpecs from './balanceTandemStance/balance_tandem_stance_v1.0.json';
import balanceTandemGaitSpecs from './balanceTandemGait/balance_tandem_gait_v1.0.json';
import treatmentPlanSpecs from './treatmentPlan/treatment_plan_v1.0.json';
import monthsInReverseSpecs from './monthsInReverse/months_in_reverse_v1.0.json';
import delayedRecallSpecs from './delayedRecall/delayed_recall_v1.0.json';
import { DumbConfig, KeyValuePair } from './modules.interface';
import { AssessmentCreationViewService } from '../../assessmentCreationView/assessmentCreation.view.service';
import { RootState, useAppSelector } from '../../../../../state/hooks';
import { AssessmentModules } from '../modules';

@injectable()
export class ModulesSpecsDomain {
  public constructor(
    private assessmentCreationViewService: AssessmentCreationViewService,
  ) {}

  glasgowCommaScaleConfig = (): DumbConfig => {
    return dumbConfig(
      glasgowCommaScaleSpecs,
      SpecType.SINGLE_CHOICE,
      InputType.ITEMS,
    );
  };

  symptomsChecklistConfig = (): DumbConfig => {
    return dumbConfig(symptomsChecklistSpecs, SpecType.LIST, InputType.RANGE);
  };

  digitsBackwardsConfig = (): DumbConfig => {
    return dumbConfig(digitsBackwardsSpecs);
  };

  neckExamConfig = (): DumbConfig => {
    return dumbConfig(neckExamSpecs, SpecType.BOOLEAN_CHOICE);
  };

  neurologicalExamConfig = (): DumbConfig => {
    return dumbConfig(neurologicalExamSpecs, SpecType.BOOLEAN_CHOICE);
  };

  singleLegBalanceExamConfig = (): DumbConfig => {
    return dumbConfig(singleLegExamSpecs);
  };

  doubleLegBalanceExamConfig = (): DumbConfig => {
    return dumbConfig(doubleLegExamSpecs);
  };

  tandemBalanceExamConfig = (): DumbConfig => {
    return dumbConfig(tandemExamSpecs);
  };

  balanceTandemGaitExamConfig = (): DumbConfig => {
    return dumbConfig(balanceTandemGaitSpecs, SpecType.BOOLEAN_CHOICE);
  };

  treatmentPlanExamConfig = (): DumbConfig => {
    return dumbConfig(treatmentPlanSpecs);
  };
  immediateMemoryExamConfig = (): DumbConfig => {
    return dumbConfig(immediateMemorySpecs);
  };
  monthsInReverseExamConfig = (): DumbConfig => {
    return dumbConfig(monthsInReverseSpecs);
  };

  delayedRecallExamConfig = (): DumbConfig => {
    return dumbConfig(delayedRecallSpecs);
  };

  useResponseSelector = (responseType: string): any => {
    return useAppSelector((state: RootState) => {
      return this.convertBoolToString(
        this.assessmentCreationViewService.assessmentModuleSelector(state),
        responseType,
      );
    });
  };

  convertBoolToString = (module: any, responseType: string) => {
    const dataToUpdate = { ...module[responseType] };
    const keys = Object.keys(dataToUpdate);
    keys.forEach((key) => {
      dataToUpdate[key as keyof KeyValuePair] = dataToUpdate[
        key as keyof KeyValuePair
      ]
        ? 'Yes' // TODO localise
        : 'No'; // TODO localise
    });
    return dataToUpdate;
  };

  glasgowComaScaleInputIsValid(module: any): boolean {
    if (!module) {
      return false;
    }

    if (module.skipModuleFlag === true) {
      return true;
    }

    let gcsEyeResponseValid: boolean = false;
    let gcsVerbalResponseValid: boolean = false;
    let gcsMotorResponseValid: boolean = false;

    if (
      // TODO: localise
      (module.gcsEyeResponse && !(module.gcsEyeResponse === 'Select')) ||
      module.gcsEyeResponseNotTested
    ) {
      gcsEyeResponseValid = true;
    }

    if (
      // TODO: localise
      (module.gcsVerbalResponse && !(module.gcsVerbalResponse === 'Select')) ||
      module.gcsVerbalResponseNotTested
    ) {
      gcsVerbalResponseValid = true;
    }

    if (
      // TODO: localise
      (module.gcsMotorResponse && !(module.gcsMotorResponse === 'Select')) ||
      module.gcsMotorResponseNotTested
    ) {
      gcsMotorResponseValid = true;
    }

    if (
      gcsEyeResponseValid &&
      gcsVerbalResponseValid &&
      gcsMotorResponseValid
    ) {
      return true;
    }

    return false;
  }

  immediateMemoryInputIsValid(module: any): boolean {
    if (!module) {
      return false;
    }

    if (module.skipModuleFlag === true) {
      return true;
    }

    if (
      !module.immediateMemoryTrial1Response ||
      !module.immediateMemoryTrial2Response ||
      !module.immediateMemoryTrial3Response
    ) {
      return false;
    }

    if (
      Object.keys(module.immediateMemoryTrial1Response).length <= 2 &&
      module.immediateMemoryTrial1Response?.noAnswersCorrect === false
    ) {
      return false;
    }

    if (
      Object.keys(module.immediateMemoryTrial2Response).length <= 2 &&
      module.immediateMemoryTrial2Response?.noAnswersCorrect === false
    ) {
      return false;
    }

    if (
      Object.keys(module.immediateMemoryTrial3Response).length <= 2 &&
      module.immediateMemoryTrial3Response?.noAnswersCorrect === false
    ) {
      return false;
    }

    return true;
  }

  neckExamInputIsValid(module: any): boolean {
    if (!module) {
      return false;
    }

    if (module.skipModuleFlag === true) {
      return true;
    }

    if (
      typeof module.neckExamTenderness !== 'undefined' &&
      typeof module.neckExamPain !== 'undefined' &&
      typeof module.neckExamPainDuringFullRangeMovement !== 'undefined'
    ) {
      return true;
    }

    return false;
  }

  neurologicalExamInputIsValid(module: any): boolean {
    if (!module) {
      return false;
    }

    if (module.skipModuleFlag === true) {
      return true;
    }

    if (
      typeof module.neuroExamPainFreePassiveCervicalSpineMovement !==
        'undefined' &&
      typeof module.neuroExamCanPerformCoordinationExamNormally !==
        'undefined' &&
      typeof module.neuroExamCanReadAloudAndFollowInstructions !==
        'undefined' &&
      typeof module.neuroExamCanLookSideToSideUpAndDownWithoutDoubleVision !==
        'undefined'
    ) {
      return true;
    }

    return false;
  }

  symptomChecklistInputIsValid(module: any): boolean {
    if (!module) {
      return false;
    }

    if (module?.skipModuleFlag === true) {
      return true;
    }

    let isValid: boolean = true;

    if (
      !(
        typeof module?.symptomHeadache === 'number' &&
        typeof module?.symptomPressureInHead === 'number' &&
        typeof module?.symptomNeckPain === 'number' &&
        typeof module?.symptomNauseaOrVomiting === 'number' &&
        typeof module?.symptomDizziness === 'number' &&
        typeof module?.symptomBlurredVision === 'number' &&
        typeof module?.symptomBalanceProblems === 'number' &&
        typeof module?.symptomSensitivityToLight === 'number' &&
        typeof module?.symptomSensitivityToNoise === 'number' &&
        typeof module?.symptomFeelingSlowedDown === 'number' &&
        typeof module?.symptomFeelingLikeInFog === 'number' &&
        typeof module?.symptomDontFeelRight === 'number' &&
        typeof module?.symptomDifficultyConcentrating === 'number' &&
        typeof module?.symptomDifficultyRemembering === 'number' &&
        typeof module?.symptomFatigueOrLowEnergy === 'number' &&
        typeof module?.symptomConfusion === 'number' &&
        typeof module?.symptomDrowsiness === 'number' &&
        typeof module?.symptomTroubleFallingAsleep === 'number' &&
        typeof module?.symptomMoreEmotional === 'number' &&
        typeof module?.symptomIrritability === 'number' &&
        typeof module?.symptomSadness === 'number' &&
        typeof module?.symptomNervousOrAnxious === 'number'
      )
    ) {
      isValid = false;
    }

    return isValid;
  }

  digitsBackwardsInputIsValid(module: any): boolean {
    if (!module) {
      return false;
    }

    if (module.skipModuleFlag === true) {
      return true;
    }

    if (!module.sequencesRecalled) {
      return false;
    }

    return true;
  }

  monthsInReverseInputIsValid(
    module: any,
    selectedMonth: string[] | undefined,
  ): boolean {
    if (!module) {
      return false;
    }

    if (module.skipModuleFlag === true) {
      return true;
    }

    if (typeof selectedMonth === 'undefined') {
      return false;
    }

    return true;
  }

  tandemGaitInputIsValid(module: any): boolean {
    if (!module) {
      return false;
    }

    if (module.skipModuleFlag === true) {
      return true;
    }

    if (typeof module.patientCanPerformTandemGaitNormally !== 'undefined') {
      return true;
    }

    return false;
  }

  balanceInputIsValid(
    module: any,
    timerStarted: boolean | undefined,
    balanceErrors: number,
  ): boolean {
    if (!module) {
      return false;
    }

    if (module.skipModuleFlag === true) {
      return true;
    }

    if (timerStarted || balanceErrors > 0) {
      return true;
    }

    return false;
  }

  private vomsSectionValid(vomsSection: any): boolean {
    return (
      vomsSection?.notTested === true ||
      (typeof vomsSection?.headache === 'number' &&
        typeof vomsSection?.dizziness === 'number' &&
        typeof vomsSection?.nausea === 'number' &&
        typeof vomsSection?.fogginess === 'number' &&
        vomsSection?.headache >= 0 &&
        vomsSection?.dizziness >= 0 &&
        vomsSection?.nausea >= 0 &&
        vomsSection?.fogginess >= 0)
    );
  }

  vomsInputIsValid(module: any): boolean {
    if (!module) {
      return false;
    }

    if (module.skipModuleFlag === true) {
      return true;
    }

    if (!this.vomsSectionValid(module.vomsBaselineSymptoms)) {
      return false;
    }
    if (!this.vomsSectionValid(module.vomsSmoothPursuits)) {
      return false;
    }
    if (!this.vomsSectionValid(module.vomsSaccadesHorizontal)) {
      return false;
    }
    if (!this.vomsSectionValid(module.vomsSaccadesVertical)) {
      return false;
    }
    if (!this.vomsSectionValid(module.vomsConvergence)) {
      return false;
    }
    if (!this.vomsSectionValid(module.vomsVORHorizontal)) {
      return false;
    }
    if (!this.vomsSectionValid(module.vomsVORVertical)) {
      return false;
    }
    if (!this.vomsSectionValid(module.vomsVisualMotionSensitivityTest)) {
      return false;
    }

    return true;
  }

  delayedRecallInputIsValid(module: any): boolean {
    if (!module) {
      return false;
    }

    if (module.skipModuleFlag === true) {
      return true;
    }

    if (module?.noAnswersCorrect === true) {
      return true;
    }

    if (module?.wordsRecalledKeyVal) {
      const len = Object.keys(module?.wordsRecalledKeyVal).length;
      if (len > 0) {
        return true;
      }
    }

    return false;
  }

  validateModule = (
    module: any,
    assessmentModule: AssessmentModules,
    timerStarted?: boolean,
    selectedMonths?: string[],
  ) => {
    if (!module) {
      return false;
    }

    switch (assessmentModule) {
      case AssessmentModules.GlasgowComaScale:
        return this.glasgowComaScaleInputIsValid(module);
      case AssessmentModules.ImmediateMemory:
        return this.immediateMemoryInputIsValid(module);
      case AssessmentModules.NeckExam:
        return this.neckExamInputIsValid(module);
      case AssessmentModules.NeurologicalExam:
        return this.neurologicalExamInputIsValid(module);
      case AssessmentModules.SymptomChecklist:
        return this.symptomChecklistInputIsValid(module);
      case AssessmentModules.DigitsBackwards:
        return this.digitsBackwardsInputIsValid(module);
      case AssessmentModules.MonthsInReverse:
        return this.monthsInReverseInputIsValid(module, selectedMonths);
      case AssessmentModules.BalanceTandemGait:
        return this.tandemGaitInputIsValid(module);
      case AssessmentModules.BalanceDoubleLegStance:
        return this.balanceInputIsValid(
          module,
          timerStarted,
          module.balanceDoubleLegStanceErrors,
        );
      case AssessmentModules.BalanceSingleLegStance:
        return this.balanceInputIsValid(
          module,
          timerStarted,
          module.balanceSingleLegStanceErrors,
        );
      case AssessmentModules.BalanceTandemStance:
        return this.balanceInputIsValid(
          module,
          timerStarted,
          module.balanceTandemStanceErrors,
        );
      case AssessmentModules.VOMS:
        return this.vomsInputIsValid(module);
      case AssessmentModules.DelayedRecall:
        return this.delayedRecallInputIsValid(module);
      default:
        return false;
    }
  };

  resetSkipModule(moduleName: string): void {
    this.assessmentCreationViewService.dispatchSetAssessmentModuleDetails(
      moduleName,
      'skipModuleFlag',
      false,
    );

    this.assessmentCreationViewService.dispatchSetAssessmentModuleDetails(
      moduleName,
      'skipModuleComments',
      '',
    );
  }
}
